import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { getFacilitiesData, getSearchValue } from '../../licenseAnalyticsSupport/getLicenseAnalyticsActions';
import { ClubNameCell } from '../finance/academyTableComponent';
import { useTranslation } from 'react-i18next';
import { facilityStatusNaming, capabilitiesNaming } from '../../../../api/typesDc';
import FacilitiesSubTableComponent from './facilitiesSubTableComponent';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Divider from '../../../../components/Divider';

// Utility function to handle empty values
const handleEmptyValue = (value:any) => (value ? value : '-');

const Row = React.memo(({ index, selectedRow, setSelectedRow, facility }:any) => {
  const [t] = useTranslation();
  const capabilities = facility?.capabilities?.map((item:any) => t(capabilitiesNaming[item])).join(', ') || '-';

  return (
    <TableRow hover selected={index === selectedRow} onClick={() => setSelectedRow(index)}>
      <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px', minWidth: '100px', maxWidth: '375px' }}>
        <ClubNameCell clubData={facility} getImageUrl={(data:any) => data?.club?.imagePath} />
      </TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px', minWidth: '100px', maxWidth: '375px' }}>
        {handleEmptyValue(facility?.facilityName)}
      </TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px', minWidth: '100px', maxWidth: '375px' }}>
        {handleEmptyValue(facility?.shortName)}
      </TableCell>
      <TableCell style={{ fontSize: '0.75rem', textAlign: 'center', padding: '0px 16px' }}>
        {facility?.facilityGrounds?.length || '-'}
      </TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px', minWidth: '100px', maxWidth: '375px' }}>{handleEmptyValue(facility?.address)}</TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px', minWidth: '100px', maxWidth: '375px' }}>{handleEmptyValue(facility?.owner)}</TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px', minWidth: '100px', maxWidth: '375px' }}>
        {t(facilityStatusNaming[facility?.facilityStatus] || '-')}
      </TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '0px 16px', minWidth: '100px', maxWidth: '600px' }}>{capabilities}</TableCell>
      <TableCell style={{ fontSize: '0.75rem', textAlign: 'center', padding: '0px 16px' }}>
        {facility?.changingRooms || '-'}
      </TableCell>
      <TableCell style={{ fontSize: '0.75rem', textAlign: 'center', padding: '0px 16px' }}>
        {facility?.dormitoryCapacity || '-'}
      </TableCell>
    </TableRow>
  );
});

const FacilitiesExplorePage = React.memo(() => {
  const facilitiesData = useSelector(getFacilitiesData);
  const searchValue = useSelector(getSearchValue);
  const [selectedRow, setSelectedRow] = useState(null);
  const [objectList, setObjectList] = useState(facilitiesData);
  const [isAscending, setIsAscending] = useState(false);
  const [isAscending1, setIsAscending1] = useState(true);
  const [criteria, setCriteria] = useState('Club');
  const [facilitiesCount, setFacilitiesCount] = useState();
  const [t] = useTranslation();
  // Memoized sorting function
  const sortedObjectList = useMemo(() => {
    return [...objectList].sort((a, b) => {
      if (criteria === 'Club') {
        const nameA = a.club.name.toLowerCase();
        const nameB = b.club.name.toLowerCase();
        return isAscending1 ? nameA?.localeCompare(nameB) : nameB?.localeCompare(nameA);
      } else {
        const nameA = a.facilityName.toLowerCase();
        const nameB = b.facilityName.toLowerCase();
        return isAscending ? nameA?.localeCompare(nameB) : nameB?.localeCompare(nameA);
      }
    });
  }, [objectList, isAscending, isAscending1]);

  // Callback for sorting to avoid recreation on every render
  const handleSortClick1 = useCallback(() => {
    setCriteria('Club');
    setIsAscending1(prev => !prev);
  }, []);

  // Callback for sorting to avoid recreation on every render
  const handleSortClick = useCallback(() => {
    setCriteria('Facility');
    setIsAscending(prev => !prev);
  }, []);

  useEffect(() => {
    if (searchValue) {
      const filteredList = facilitiesData.filter((item:any) => {
        return (
          item.facilityName.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.club.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.shortName.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.address.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.facilityGrounds?.length
            .toString()
            .toLowerCase()
            .includes(searchValue.toLowerCase()) ||
          item.owner.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.facilityStatus.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.changingRooms
            .toString()
            .toLowerCase()
            .includes(searchValue.toLowerCase()) ||
          item.dormitoryCapacity
            .toString()
            .toLowerCase()
            .includes(searchValue.toLowerCase())
        );
      });
      setObjectList(filteredList);
      setFacilitiesCount(filteredList?.length);
    } else {
      setObjectList(facilitiesData);
      setFacilitiesCount(facilitiesData?.length);
    }
  }, [searchValue, facilitiesData]);

  const rowsSkeleton = Array.from({ length: 10 }).map((_, index) => (
    <TableRow key={index}>
      {Array.from({ length: 10 }).map((_, cellIndex) => (
        <TableCell key={cellIndex}>
          <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="100%" height={20} />
        </TableCell>
      ))}
    </TableRow>
  ));

  return (
    <>
      <Typography sx={{ width: '33%', flexShrink: 0, fontWeight: 'bold', fontFamily: 'Montserrat', padding: '6px' }}>
        {t('DC_6_FACILITY') + ' ' + t('DETAILS') + ` (${facilitiesCount} ${t('ROWS')})`}
      </Typography>
      <Divider />
      <TableContainer className={'table-container'} sx={{ backgroundColor: '#f3f6fb', maxHeight: '600px' }}>
        <Table stickyHeader aria-label="collapsible table" sx={{ width: 'max-content' }}>
          <TableHead>
            <TableRow>
              <TableCell style={{ backgroundColor: '#f3f6fb', padding: '6px 16px', fontWeight: 'bold', minWidth: '200px' }}>
                {t('CLUB_NAME')}
                {!isAscending1 ? (
                  <ArrowDropUpIcon onClick={() => handleSortClick1()} />
                ) : (
                  <ArrowDropDownIcon onClick={() => handleSortClick1()} />
                )}
              </TableCell>
              <TableCell style={{ backgroundColor: '#f3f6fb', padding: '6px 16px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {t('DC_6_FACILITY_NAME')}
                {isAscending ? (
                  <ArrowDropUpIcon onClick={() => handleSortClick()} />
                ) : (
                  <ArrowDropDownIcon onClick={() => handleSortClick()} />
                )}
              </TableCell>
              <TableCell style={{ backgroundColor: '#f3f6fb', padding: '6px 16px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {t('DC_6_SHORT_NAME')}
              </TableCell>
              <TableCell
                style={{
                  backgroundColor: '#f3f6fb',
                  padding: '6px 16px',
                  fontWeight: 'bold',
                  minWidth: '100px',
                  paddingLeft: '7px',
                  textAlign: 'center',
                }}
              >
                {t('DC_6_GROUNDS')}
              </TableCell>
              <TableCell style={{ backgroundColor: '#f3f6fb', padding: '6px 16px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {t('DC_6_ADDRESS')}
              </TableCell>
              <TableCell style={{ backgroundColor: '#f3f6fb', padding: '6px 16px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {t('OWNER')}
              </TableCell>
              <TableCell style={{ backgroundColor: '#f3f6fb', padding: '6px 16px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {t('STATUS')}
              </TableCell>
              <TableCell style={{ backgroundColor: '#f3f6fb', padding: '6px 16px', fontWeight: 'bold', minWidth: '200px' }}>
                {t('DC_6_CAPABILITIES')}
              </TableCell>
              <TableCell
                style={{
                  backgroundColor: '#f3f6fb',
                  padding: '6px 16px',
                  fontWeight: 'bold',
                  minWidth: '150px',
                  paddingLeft: '7px',
                  textAlign: 'center',
                }}
              >
                {t('DC_CAP_CHANGING_ROOMS')}
              </TableCell>
              <TableCell
                style={{
                  backgroundColor: '#f3f6fb',
                  padding: '6px 16px',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  paddingLeft: '7px',
                  textAlign: 'center',
                }}
              >
                {t('DC_6_DORMITORY_CAPACITY')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {facilitiesData && facilitiesData.length > 0 ? (
              sortedObjectList.length > 0 ? (
                sortedObjectList.map((facility, index) => (
                  <Row
                    key={index}
                    index={index}
                    selectedRow={selectedRow}
                    setSelectedRow={setSelectedRow}
                    facility={facility}
                  />
                ))
              ) : searchValue === '' ? (
                rowsSkeleton
              ) : (
                <TableRow>
                  <TableCell colSpan={10} style={{ textAlign: 'center' }}>
                    {t('No_Matching_Records_Found')}...
                  </TableCell>
                </TableRow>
              )
            ) : facilitiesData?.length == 0 ? (
              <TableRow>
                <TableCell colSpan={10} style={{ textAlign: 'center' }}>
                  {t('No_Matching_Records_Found')}...
                </TableCell>
              </TableRow>
            ) : (
              rowsSkeleton
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {selectedRow !== null && objectList[selectedRow] && (
        <div style={{ paddingTop: '20px', width: 'min-content' }}>
          <FacilitiesSubTableComponent clubData={objectList[selectedRow]} setSelectedRow={setSelectedRow} />
        </div>
      )}
    </>
  );
});

export default FacilitiesExplorePage;
